import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 60000 });
let accessTokenProvider = async () => null;
let unauthorizedHandler = null;

export function configureApiAuth({ getAccessToken, onUnauthorized } = {}) {
  accessTokenProvider = getAccessToken || (async () => null);
  unauthorizedHandler = onUnauthorized || null;
}

export async function getAccessToken() {
  return accessTokenProvider();
}

client.interceptors.request.use(async config => {
  const token = await accessTokenProvider();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && unauthorizedHandler) {
      unauthorizedHandler();
    }
    return Promise.reject(error);
  },
);

export const api = {
  me: () => client.get(`/auth/me`).then(r => r.data),
  // generic CRUD
  list: (path, params = {}) => client.get(`/${path}`, { params }).then(r => r.data),
  get: (path, id) => client.get(`/${path}/${id}`).then(r => r.data),
  create: (path, body) => client.post(`/${path}`, body).then(r => r.data),
  patch: (path, id, body, expectedRevision = null) => client.patch(`/${path}/${id}`, body, {
    params: expectedRevision == null ? {} : { expected_revision: expectedRevision },
  }).then(r => r.data),
  remove: (path, id, expectedRevision = null) => client.delete(`/${path}/${id}`, {
    params: expectedRevision == null ? {} : { expected_revision: expectedRevision },
  }).then(r => r.data),

  // domain
  dashboard: () => client.get(`/dashboard`).then(r => r.data),
  planner: (id) => client.get(`/port-calls/${id}/planner`).then(r => r.data),
  personnelCandidates: (id, body) => client.post(`/port-calls/${id}/personnel-candidates`, body).then(r => r.data),
  personnelLaborSummary: (id) => client.get(`/personnel/${id}/labor-summary`).then(r => r.data),
  deactivateLaborGroup: (id) => client.post(`/labor-groups/${id}/deactivate`).then(r => r.data),
  createWorkerCase: (personnelId, body) => client.post(`/personnel/${personnelId}/labor-cases`, body).then(r => r.data),
  versionWorkerCase: (caseId, body, expectedRevision) => client.patch(`/labor-cases/${caseId}`, body, { params: { expected_revision: expectedRevision } }).then(r => r.data),
  activateWorkerCase: (caseId, body) => client.post(`/labor-cases/${caseId}/activate`, body).then(r => r.data),
  resolveWorkerCase: (caseId, body) => client.post(`/labor-cases/${caseId}/resolve`, body).then(r => r.data),
  aiExplain: (id) => client.post(`/port-calls/${id}/ai/explain`).then(r => r.data),
  aiRecommend: (id) => client.post(`/port-calls/${id}/ai/recommend`).then(r => r.data),
  submit: (id, expectedRevision) => client.post(`/port-calls/${id}/submit`, null, { params: { expected_revision: expectedRevision } }).then(r => r.data),
  approve: (id, body) => client.post(`/port-calls/${id}/approve`, body).then(r => r.data),
  positionAssignment: (id, body) => client.patch(`/assignments/${id}/position`, body).then(r => r.data),
  autoPositionAssignment: (id) => client.post(`/assignments/${id}/auto-position`).then(r => r.data),
  conditions: (terminalId, refresh = false) =>
    client.get(`/conditions/${terminalId}`, { params: { refresh } }).then(r => r.data),
  manualCondition: (body) => client.post(`/conditions/manual`, body).then(r => r.data),
  audit: (params = {}) => client.get(`/audit`, { params }).then(r => r.data),

  uploadMap: (file, terminalId, scale = 1.0) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("terminal_id", terminalId);
    fd.append("scale_m_per_px", String(scale));
    return client.post(`/maps/upload`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then(r => r.data);
  },
  autoDetectMap: (mapId, replace = false, max_pages = 5) =>
    client.post(`/maps/${mapId}/auto-detect-annotations`, null, { params: { replace, max_pages }, timeout: 300000 }).then(r => r.data),
  acceptAnnotation: (id) => client.post(`/map-annotations/${id}/accept`).then(r => r.data),
  rejectAnnotation: (id) => client.post(`/map-annotations/${id}/reject`).then(r => r.data),
  acceptAllProposed: (mapId) => client.post(`/maps/${mapId}/accept-all-proposed`).then(r => r.data),
  rejectAllProposed: (mapId) => client.post(`/maps/${mapId}/reject-all-proposed`).then(r => r.data),
  visionStats: (terminalId) => client.get(`/vision/stats`, { params: terminalId ? { terminal_id: terminalId } : {} }).then(r => r.data),
  sopAgentParse: (body) => client.post(`/sop-agent/parse`, body, { timeout: 60000 }).then(r => r.data),
  sopAgentAccept: (body) => client.post(`/sop-agent/accept`, body).then(r => r.data),
  proposeLaborRules: (body) => client.post(`/labor-rules/ai/propose`, body, { timeout: 60000 }).then(r => r.data),
  approveLaborRule: (id) => client.post(`/labor-rules/${id}/approve`).then(r => r.data),
  deactivateLaborRule: (id) => client.post(`/labor-rules/${id}/deactivate`).then(r => r.data),
  uploadLaborAgreement: (file, metadata) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(metadata).forEach(([key, value]) => fd.append(key, value ?? ""));
    return client.post(`/labor-agreements`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: 120000,
    }).then(r => r.data);
  },
  laborAgreementComponents: (id, params = {}) =>
    client.get(`/labor-agreements/${id}/components`, { params }).then(r => r.data),
  importGeofile: (mapId, file, bbox = null) => {
    const fd = new FormData(); fd.append("file", file);
    if (bbox) fd.append("bbox", bbox);
    return client.post(`/maps/${mapId}/import-geofile`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then(r => r.data);
  },
  importOsm: (mapId, bbox) => {
    const fd = new FormData(); fd.append("bbox", bbox);
    return client.post(`/maps/${mapId}/import-osm`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 60000 }).then(r => r.data);
  },
  snapEdges: (id) => client.post(`/map-annotations/${id}/snap-edges`).then(r => r.data),
  snapAllProposed: (mapId, searchPx = 15) =>
    client.post(`/maps/${mapId}/snap-all-proposed`, null, { params: { search_px: searchPx }, timeout: 120000 }).then(r => r.data),
  setBoundary: (terminalId, points) =>
    client.put(`/terminals/${terminalId}/boundary`, { points }).then(r => r.data),
  clearBoundary: (terminalId) =>
    client.delete(`/terminals/${terminalId}/boundary`).then(r => r.data),
  readGeoref: (mapId) => client.get(`/maps/${mapId}/georef`).then(r => r.data),
  mapConfig: (terminalId) => client.get(`/terminals/${terminalId}/map-config`).then(r => r.data),
  saveMapConfig: (terminalId, body, expectedRevision) => client.put(`/terminals/${terminalId}/map-config`, body, { params: { expected_revision: expectedRevision } }).then(r => r.data),
  fileBlob: (path) => client.get(path, { responseType: "blob" }).then(r => r.data),
  mapFileBlob: (mapId) => client.get(`/maps/${mapId}/file`, { responseType: "blob" }).then(r => r.data),
  laborAgreementFileBlob: (id) => client.get(`/labor-agreements/${id}/file`, { responseType: "blob" }).then(r => r.data),

  // Five-stage map pipeline (SVG / vector PDF / ASCII DXF + optional XLSX context)
  processMapPipeline: (terminalId, files, sourceType = "auto") => {
    const fd = new FormData();
    const sourceFiles = Array.isArray(files) ? files : [files];
    sourceFiles.filter(Boolean).forEach(file => fd.append("file", file));
    return client.post(
      `/terminals/${terminalId}/process-map`,
      fd,
      {
        params: { source_type: sourceType },
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      }
    ).then(r => r.data);
  },
  latestPipeline: (terminalId) =>
    client.get(`/terminals/${terminalId}/map-pipelines/latest`).then(r => r.data),
  pipelineStatus: (terminalId, pipelineId) =>
    client.get(`/terminals/${terminalId}/pipeline-status/${pipelineId}`).then(r => r.data),
  reviewPipeline: (pipelineId, action, annotationIds = []) =>
    client.post(`/map-pipelines/${pipelineId}/review`, { action, annotation_ids: annotationIds }).then(r => r.data),
  publishPipeline: (pipelineId, notes = "") =>
    client.post(`/map-pipelines/${pipelineId}/publish`, { notes }).then(r => r.data),
  exportPipeline: (pipelineId) =>
    client.get(`/map-pipelines/${pipelineId}/export`, { responseType: "blob" }).then(r => r.data),
};

export default api;
